/**
 * Contexte actif — pastille + palette de changement.
 * Doctrine : Noam ne change JAMAIS le contexte par inférence ; le changement
 * est une action utilisateur explicite ici. Stockage unique côté serveur.
 */
import { useEffect, useRef, useState } from "react";
import { Building2, ChevronDown, Clock, Search, GitBranch, Globe2, Handshake } from "lucide-react";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";

const scopeIcon = (o) =>
  o.scope_kind === "self" ? Building2 : o.scope_kind === "collection" ? GitBranch
    : o.object_type === "deal" ? Handshake : Globe2;

export default function ActiveContextBar({ onChange }) {
  const { t, i18n } = useTranslation();
  const en = (i18n.language || "fr").startsWith("en");
  const [ctx, setCtx] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [options, setOptions] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    api.get("/workspace/agentic/context").then((r) => {
      setCtx(r.data.context); setOrgName(r.data.org_name || "");
      onChange?.(r.data.context);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    const h = setTimeout(() => {
      api.get(`/workspace/agentic/context/options?q=${encodeURIComponent(q)}`)
        .then((r) => setOptions(r.data)).catch(() => {});
    }, 200);
    return () => clearTimeout(h);
  }, [open, q]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const select = async (o) => {
    try {
      const r = await api.put("/workspace/agentic/context", {
        scope_kind: o.scope_kind, collection_key: o.collection_key,
        object_type: o.object_type, object_id: o.object_id,
      });
      setCtx(r.data.context); setOpen(false); setQ("");
      onChange?.(r.data.context);
    } catch { /* noop */ }
  };

  if (!ctx) return null;
  // Qualification dérivée de l'ontologie (engagement) ; fallback honnête sinon
  const engagement = ctx.engagement ? (en ? ctx.engagement.en : ctx.engagement.fr) : null;
  const scopeLabel = ctx.scope_kind === "self" ? (en ? "My organization" : "Mon organisation")
    : ctx.scope_kind === "collection" ? (en ? "My pipeline" : "Mon pipeline")
      : ctx.object_type === "deal" ? "Deal"
        : engagement || (en ? "External organization" : "Organisation externe");
  const isObj = ctx.scope_kind === "object";

  const Row = ({ o, active, hint }) => {
    const Ic = scopeIcon(o);
    return (
      <button onClick={() => select(o)} data-testid={`context-option-${(o.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition-colors ${active ? "bg-[#43C6D8]/10 border border-[#43C6D8]/30" : "hover:bg-white/[0.05]"}`}>
        <Ic className={`w-4 h-4 shrink-0 ${active ? "text-[#43C6D8]" : "text-zinc-400"}`} />
        <span className="flex-1 min-w-0">
          <span className={`block text-[13px] truncate ${active ? "text-white" : "text-zinc-300"}`}>{o.label}</span>
          {hint && <span className="block text-[11px] text-zinc-600 truncate">{hint}</span>}
        </span>
        {active && <span className="text-[9px] uppercase tracking-[0.15em] text-[#43C6D8]">{en ? "Active" : "Actif"}</span>}
      </button>
    );
  };

  const same = (o) => (o.scope_kind === ctx.scope_kind
    && (o.scope_kind !== "object" || o.object_id === ctx.object_id)
    && (o.scope_kind !== "collection" || o.collection_key === (ctx.collection_key || "pipeline")));

  return (
    <div className="relative inline-block" ref={boxRef} data-testid="active-context-bar">
      <button onClick={() => setOpen((v) => !v)} data-testid="active-context-chip"
        className="inline-flex items-center gap-2 border border-white/10 bg-white/[0.03] rounded-full pl-3 pr-2 py-1.5 text-xs hover:border-white/25 transition-colors max-w-full">
        <span className="text-zinc-500 hidden sm:inline">{en ? "Context" : "Contexte"}</span>
        <span className="text-zinc-600 hidden sm:inline">·</span>
        <Building2 className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
        <span className="text-zinc-300 hidden sm:inline truncate max-w-[140px]">{orgName}</span>
        <span className="text-zinc-600 hidden sm:inline">·</span>
        <span className={`font-medium truncate ${isObj ? "text-[#EF9F27]" : "text-[#43C6D8]"}`}>{scopeLabel}</span>
        {isObj && (<><span className="text-zinc-600">·</span><span className="text-white font-medium truncate max-w-[160px]">{ctx.label}</span></>)}
        <ChevronDown className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-40 mt-2 w-[min(420px,90vw)] border border-white/15 bg-[#0B1017] rounded-xl shadow-2xl overflow-hidden" data-testid="context-palette">
          <div className="px-3 py-2.5 border-b border-white/5 flex items-center gap-2">
            <Search className="w-4 h-4 text-zinc-500 shrink-0" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={en ? "What are you working on? (deal, company…)" : "Sur quoi travaillez-vous ? (deal, société…)"}
              data-testid="context-palette-search"
              className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-zinc-600" />
          </div>
          <div className="px-2 py-2 max-h-[320px] overflow-y-auto">
            <div className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-zinc-600">{en ? "Scopes" : "Périmètres"}</div>
            {(options?.scopes || []).map((o) => (
              <Row key={o.scope_kind + (o.collection_key || "")}
                o={o} active={same(o)}
                hint={o.scope_kind === "self" ? (en ? "Governance, risks, decisions" : "Gouvernance, risques, décisions") : (en ? "Deals, leads, financing" : "Deals, leads, financements")} />
            ))}
            {(q ? (options?.results || []) : (options?.recents || [])).length > 0 && (
              <div className="px-2 py-1 mt-1 text-[10px] uppercase tracking-[0.2em] text-zinc-600 flex items-center gap-1.5">
                {q ? <Search className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {q ? (en ? "Results" : "Résultats") : (en ? "Recent" : "Récents")}
              </div>
            )}
            {(q ? (options?.results || []) : (options?.recents || [])).map((o) => (
              <Row key={o.object_id} o={o} active={same(o)}
                hint={o.object_type === "deal" ? "Deal" : (en ? "External organization" : "Organisation externe")} />
            ))}
          </div>
          <div className="px-3 py-2 border-t border-white/5 text-[10px] text-zinc-600">
            {en ? "The ontology holds all Noam knows · the active context defines what matters now"
              : "L'ontologie contient tout ce que Noam sait · le contexte actif définit ce qui compte maintenant"}
          </div>
        </div>
      )}
    </div>
  );
}
